import { Component, computed, DestroyRef, inject, LOCALE_ID, OnInit, output, signal } from '@angular/core';
import { InfoField } from '../../../../../shared/ui/view-details/view-details.model';
import { ViewDetails } from '../../../../../shared/ui/view-details/view-details';
import { FileAttachmentItem } from '../../../../../shared/ui/user-view-file-image-details/user-view-file-image-details.model';
import { UserViewFileImageDetails } from '../../../../../shared/ui/user-view-file-image-details/user-view-file-image-details';
import { ApprovedPharmaciesService } from '../../../../../services/pharmacy/approved-pharmacies.service';
import { ActivatedRoute } from '@angular/router';
import { PharmacyDetails } from '../../../../../pages/admin-dashboard/admin-approved-pharmacies/approved-ph-details/approved-ph-details.model';
import { Error } from '../../../../public-user-component/auth/shared/error/error';
import { Loading } from '../../../../../utils/loading/loading';
@Component({
  selector: 'app-ph-details-info-tap',
  imports: [ViewDetails, UserViewFileImageDetails, Error, Loading],
  templateUrl: './ph-details-info-tap.html',
  styleUrl: './ph-details-info-tap.scss',
})
export class PhDetailsInfoTap implements OnInit {
  private localeId = inject(LOCALE_ID);
  private route = inject(ActivatedRoute);
  private pharmacyService = inject(ApprovedPharmaciesService);
  private destroyRef = inject(DestroyRef);
  isTeleSalesAssigned = output<boolean>();
  pharmacyDetails = signal<PharmacyDetails | null>(null);
  loading = signal(false);
  error = signal<any>(null);
  isSuspendedChange = output<boolean>();
  ngOnInit(): void {
    const pharmacyId = Number(this.route.snapshot.paramMap.get('id'));
    if (pharmacyId) {
      this.loadPharmacyDetails(pharmacyId);
    }
  }
  loadPharmacyDetails(pharmacyId: number): void {
    this.loading.set(true);
    this.error.set(null);
    const subscription = this.pharmacyService.getPharmacyDetails(pharmacyId).subscribe({
      next: (res) => {
        this.pharmacyDetails.set(res);
        this.isSuspendedChange.emit(res.isSuspended);
        this.loading.set(false);
        this.isTeleSalesAssigned.emit(!!res.teleSalesEmployee);
      },
      error: (err) => {
        this.error.set(err);
        this.loading.set(false);
      },
    });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }
  pharmacyInfoTitle = $localize`:@@pharmacyInfoTitle:Pharmacy Information`;
  representativeTitle = $localize`:@@representativeTitle:Representative`;
  pharmacyAddressTitle = $localize`:@@pharmacyAddressTitle:Pharmacy Address`;
  pharmacyClassificationTitle = $localize`:@@pharmacyClassificationTitle:Pharmacy Classification`;
  isArabic = computed(() => this.localeId.startsWith('ar'));
  pharmacyInfoFields = computed<InfoField[]>(() => {
    const pharmacy = this.pharmacyDetails();
    if (!pharmacy) {
      return [];
    }
    return [
      { label: this.isArabic() ? 'الاسم التجاري للصيدلية' : 'Pharmacy Trade Name', value: pharmacy.nickName },
      { label: this.isArabic() ? 'مالك الصيدلية' : 'Pharmacy Owner', value: pharmacy.owner?.fullName ?? '-' },
      {
        label: this.isArabic() ? 'رقم موبايل' : 'Mobile Number',
        value: pharmacy.pharmacyOwnerPhoneNumber ?? '-',
        fieldType: 'mobile',
      },
      { label: this.isArabic() ? 'الرقم الضريبي' : 'Tax Number', value: pharmacy.taxIdentificationNumber ?? '-' },
      {
        label: this.isArabic() ? 'رقم التسجيل التجاري' : 'Commercial Registration Number',
        value: pharmacy.commercialRegistrationNumber ?? '-',
      },
      { label: this.isArabic() ? 'الرقم القومي' : 'National ID', value: pharmacy.pharmacyOwnerNationalId ?? '-' },
    ];
  });
  representativeFields = computed<InfoField[]>(() => {
    const pharmacy = this.pharmacyDetails();
    if (!pharmacy) {
      return [];
    }
    return [
      { label: this.isArabic() ? 'المندوب' : 'Sales Representative', value: pharmacy.representative?.fullName ?? '-' },
      { label: this.isArabic() ? 'تليسيلز' : 'Telesales', value: pharmacy.teleSalesEmployee?.fullName ?? '-' },
    ];
  });
  addressFields = computed<InfoField[]>(() => {
    const pharmacy = this.pharmacyDetails();
    if (!pharmacy) {
      return [];
    }
    return [
      { label: this.isArabic() ? 'العنوان' : 'Address', value: pharmacy.address ?? '-' },
      {
        label: this.isArabic() ? 'المحافظة' : 'Governorate',
        value: this.isArabic() ? (pharmacy.governorate?.titleAr ?? '-') : (pharmacy.governorate?.titleEn ?? '-'),
      },
      {
        label: this.isArabic() ? 'المدينة' : 'City',
        value: this.isArabic() ? (pharmacy.city?.titleAr ?? '-') : (pharmacy.city?.titleEn ?? '-'),
      },
      {
        label: this.isArabic() ? 'المنطقة' : 'District',
        value: this.isArabic() ? (pharmacy.region?.titleAr ?? '-') : (pharmacy.region?.titleEn ?? '-'),
      },
      ...(pharmacy.location
        ? [
            {
              label: this.isArabic() ? 'لوكيشن الصيدلية' : 'Pharmacy Location',
              value: pharmacy.location,
              valueClass: 'underline cursor-pointer truncate max-w-[200px] block [direction:ltr] text-right',
              type: 'link' as const,
            },
          ]
        : []),
    ];
  });
  categoryFields = computed<InfoField[]>(() => {
    const pharmacy = this.pharmacyDetails();
    if (!pharmacy) {
      return [];
    }
    return [
      {
        label: this.isArabic() ? 'شريحة الصيدلية' : 'Pharmacy Segment',
        value: this.isArabic()
          ? (pharmacy.pharmacySegment?.titleAr ?? '-')
          : (pharmacy.pharmacySegment?.titleEn ?? '-'),
      },
      {
        label: this.isArabic() ? 'رصيد الصيدلية' : 'Pharmacy Balance',
        value: String(pharmacy.pharmacyCredit?.remainingCredit ?? 0),
        unit: this.isArabic() ? 'ج.م' : 'EGP',
        fieldType: 'number',
      },
    ];
  });
  documentsList = computed<FileAttachmentItem[]>(() => {
    const pharmacy = this.pharmacyDetails();
    if (!pharmacy) {
      return [];
    }
    return [
      {
        fid: pharmacy.commercialRegistrationFid,
        label: this.isArabic() ? 'السجل التجاري' : 'Commercial Register',
        fileName: this.isArabic() ? 'السجل التجاري' : 'Commercial Register',
        fileType: 'pdf',
      },
      {
        fid: pharmacy.taxRegistrationCertificateFid,
        label: this.isArabic() ? 'شهادة التسجيل الضريبي' : 'Tax Registration Certificate',
        fileName: this.isArabic() ? 'شهادة التسجيل الضريبي' : 'Tax Registration Certificate',
        fileType: 'pdf',
      },
      {
        fid: pharmacy.pharmacyContractFid,
        label: this.isArabic() ? 'عقد الصيدلية' : 'Pharmacy Contract',
        fileName: this.isArabic() ? 'عقد الصيدلية' : 'Pharmacy Contract',
        fileType: 'pdf',
      },
      {
        fid: pharmacy.pharmacyLicenseFid,
        label: this.isArabic() ? 'ترخيص الصيدلية' : 'Pharmacy License',
        fileName: this.isArabic() ? 'ترخيص الصيدلية' : 'Pharmacy License',
        fileType: 'pdf',
      },
      {
        fid: pharmacy.pharmacistNationalIdFid,
        label: this.isArabic() ? 'صورة البطاقة الشخصية' : 'National ID Copy',
        fileName: this.isArabic() ? 'صورة البطاقة الشخصية' : 'National ID Copy',
        fileType: 'image',
      },
    ];
  });
}
